// The stored save's shape, its version stamp, and whether the log it carries
// can be believed (§29 U5).
//
// Beside `runSave.ts` rather than in it: that file is reading, writing and the
// run clock, and this is the *format* — what a save looks like, how an older
// one is carried forward, and the one check that decides whether the history
// riding along with it still describes the board it was saved with.
//
// No storage and no I/O, so it is testable as arithmetic.

import { hashState } from "src/engine/rules/hash";
import { applyCommand, type Command, newGame } from "src/engine/rules/commands";
import type { Level } from "src/engine/ai/difficulty";

/**
 * The version stamp, bumped only when a stored state could not possibly be read.
 *
 * §29 U5 asks for a stamp and a migration list; this is the stamp, kept under
 * the name every save already carries. `MIGRATIONS` below is the list.
 *
 * Not the build id: that changes on every deploy and would throw away a run
 * for a CSS fix, which is the thing this format exists to avoid.
 *
 * 3: `Position.kingKind`. A save written without it decodes to a position
 * with no such array, and the first thing that asks whether a king runs a
 * factory reads `undefined[side]` and throws — so it has to be refused up
 * front rather than discovered halfway through a board.
 *
 * 4: `Position.disguise` (§26 C12). The costume moved off `IndustryState`
 * because `canTake` reads it, so an older save decodes to a position with no
 * such map and `canonicalize` reads `undefined.size` — the same shape as 3.
 *
 * 5: `Position.plating` — armor. The same shape a third time.
 *
 * 6: `Position.variant` — the Marshal and the Drone-Assisted Miner. A fourth
 * time, and for once not only the `undefined.size` crash: a save written before
 * the field would decode to a board whose pawns are plain, which is a *silent*
 * loss of material somebody paid for rather than a throw. Refused up front for
 * both reasons.
 *
 * 8: `Position.empowered` and `IndustryState.preachedAt` — the Confessor. Back
 * to 3's shape at the position end (a save without the field decodes to a board
 * whose `empowered` is `undefined`, which reads as "no grant" and is *right*,
 * since a grant never outlives the turn it was made on) — but the cooldown is
 * 7's silent kind: an old save would hand every Confessor a free empower.
 *
 * 7: the Matriarch moved the *other way* — `IndustryState.matriarch` is gone
 * and she is a `Position.variant` row. So this is 6's silent half without its
 * loud half: a save written at 6 decodes cleanly into a board where a crowned
 * queen is an ordinary queen, having quietly stopped forging, and nothing
 * anywhere throws. The one bump so far where the old save is *readable*, which
 * is exactly why it has to be refused.
 *
 * 9: the combat numbers (§31 K) — `Position.lastMoved` / `lastHurt` /
 * `platingCap`, and base armor stamped at birth. 7's silent kind: an older save
 * decodes (the maps are restored empty) into a board whose knights and rooks
 * never got their plate, so it is refused rather than half-carried.
 *
 * 10: materials and fuel (§31 M) — `IndustryState.fuel` (an older save decodes
 * with no such map and the first tick reads `undefined.get`), and the forge
 * bills and metal ladder moved under every held unit. 3's loud kind.
 */
export const FORMAT = 10;

export interface StoredRun {
  format: number;
  state: unknown;
  /** The state's hash when it was written, as a corruption check. */
  hash: number;
  /**
   * The commands the snapshot was reached by, for undo and history (§29 U5).
   *
   * **Raw, and only ever a hint.** `loadRun` replays it and checks the result
   * against `hash` before handing it back — a log only means anything against
   * the rules that produced it, which is the same fact that makes the snapshot
   * the save of record. Absent in a save written before this, and absent when
   * the store had no self-contained log to give (see `saveRun`).
   *
   * Commands are plain JSON — they already cross the wire and `roomLog.ts`
   * writes them a line each — so the codec has nothing to do here.
   */
  log?: readonly Command[];
  /** For the menu, so it can describe the save without decoding it. */
  level: string;
  plies: number;
  /**
   * How hard the run was asked to get.
   *
   * Not in the state — it is what the *player* chose, not something the board
   * knows — and so the one thing about a run that a snapshot of the board
   * cannot restore. Without it, continuing dropped back to a hardcoded default
   * and a run picked as Easy came back at full strength on the reload, which
   * reads as the game cheating rather than as a setting being lost.
   *
   * Optional because a save written before this existed has none, and throwing
   * away somebody's run over a difficulty chip is exactly the trade this whole
   * file exists to refuse.
   */
  difficulty?: Level;
  /** Epoch millis. Written by the caller, never read by the rules. */
  saved: number;
  /**
   * Total time with this run open, in milliseconds, across every session.
   *
   * **Here rather than in the state, and that is not a filing decision.**
   * `src/engine/rules/` may not read a clock — two networked clients derive one board
   * from one log, and a rule that asked the time would make the game depend on
   * how fast a machine is. There is a lint rule over those paths that fails the
   * build on `Date.now`. So elapsed time cannot be game state at all, and the
   * save wrapper is the natural place: it already carries `saved`, which is the
   * other fact about a run that the board itself does not know.
   *
   * Optional, because every save written before this has none. A run coming
   * back with no total starts counting from now rather than being refused —
   * the same trade `difficulty` makes.
   */
  played?: number;
}

/**
 * Carry a save forward one stamp at a time: `format n` → `n + 1`.
 *
 * **Empty, and that is the point of having it** (§29 U5). Every bump so far
 * refused the old save because the state's *shape* had moved under it, which is
 * a decision this table cannot make; what it exists for is the next bump that
 * is a rename or a default, where throwing a run away would be gratuitous.
 * Adding one is one entry, and `migrate` walks the chain.
 */
const MIGRATIONS: Record<number, (save: StoredRun) => StoredRun> = {};

/**
 * The stored save at today's format, or null where it cannot get there.
 *
 * **Before the decode, never after**: a step exists precisely because the
 * stored shape is not the one `decode` expects.
 */
export function migrate(save: StoredRun): StoredRun | null {
  let at = save;
  // The stamp has to *rise* every step, so a step that forgets to bump its own
  // format is a refused save rather than a loop that hangs the menu.
  while (at.format < FORMAT) {
    const step = MIGRATIONS[at.format];
    if (!step) return null;
    const next = step(at);
    if (next.format <= at.format) return null;
    at = next;
  }
  return at.format === FORMAT ? at : null;
}

/**
 * A log worth saving is one that replays from `newGame()`.
 *
 * **The test is the first command**, because `new-game` discards whatever it is
 * applied to: a log that opens with one reproduces itself from a fresh game
 * whatever base the store was measuring against. A log that does not — the one
 * a continued snapshot-only run grows, which starts at whatever the player did
 * next — could never verify, so saving it would buy nothing and spend the
 * notice on every reload from then on.
 */
export const selfContained = (log?: readonly Command[]): readonly Command[] | undefined =>
  log && log.length > 0 && log[0].type === "new-game" ? log : undefined;

/**
 * Does this log still produce the board that was saved?
 *
 * **From `newGame()`, which is where a run's log begins**: the run dispatches
 * `new-game` into a store whose base is already a fresh game, and `saveRun`
 * refuses any log that does not open with one. `GameStore.load` replays from
 * the same place, so a log that passes here lands the same board there.
 *
 * A command the rules no longer allow throws `IllegalCommandError` out of
 * `applyCommand`; that is the *expected* failure after a deploy, not an
 * exception to report, and it reads the same as a hash that has moved.
 *
 * **This is the second of two replays** — `GameStore.load` does it again to
 * fill `GameHistory`, which needs every intermediate board anyway. Once per
 * Continue, on a log a person played by hand: cheap enough not to trade the
 * store's one clean door for.
 */
export function verifies(log: readonly Command[], hash: number): boolean {
  try {
    let state = newGame();
    for (const command of log) state = applyCommand(state, command);
    return hashState(state) === hash;
  } catch {
    return false;
  }
}
